import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone, Shield } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { getIndustryById } from '@/data/industries'
import { companyInfo } from '@/data/company'

export function Banking() {
  const industry = getIndustryById('banking')!

  return (
    <>
      <SEO
        title="Technology Solutions for Banks & Financial Services"
        description="Enterprise-grade technology solutions that help banks modernize operations, enhance customer engagement, and maintain regulatory compliance."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">Banking Solutions</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                IT Cost Optimization for <span className="text-gradient">Banks</span>
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                Empowering banks to modernize their technology stack while drastically reducing operational costs through our proprietary technology brokerage model.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="glow" className="group">
                    Get Banking Quote
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-6">
                  {industry.stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      className="text-center"
                    >
                      <div className="text-3xl md:text-4xl font-heading font-bold text-gradient mb-2">
                        {stat.value}
                      </div>
                      <div className="text-sm text-slate-400">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Challenges We Solve</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Modern Banking Requires Modern Solutions
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              We understand the unique challenges facing banks in today's digital landscape.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {industry.challenges.map((challenge, index) => (
              <motion.div
                key={challenge.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-warning-500/20 flex items-center justify-center">
                        <span className="text-warning-500 font-bold">{index + 1}</span>
                      </div>
                      {challenge.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {challenge.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Our Solutions</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Enterprise Solutions for Banking
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {industry.solutions.map((solution, index) => (
              <motion.div
                key={solution.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-4">
                      <solution.icon className="w-7 h-7 text-accent-400" />
                    </div>
                    <CardTitle>{solution.title}</CardTitle>
                    <CardDescription className="text-base">
                      {solution.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Security & Compliance</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Built for Banking Security Standards
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Our solutions are designed from the ground up to meet the rigorous security and compliance requirements of financial institutions.
              </p>

              <div className="space-y-4">
                {[
                  'End-to-end encryption for all communications',
                  'SOC 2 Type II certified infrastructure',
                  'FFIEC guidance compliance',
                  'Regular penetration testing',
                  'Comprehensive audit logging',
                  'Disaster recovery and business continuity',
                ].map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                Regulatory Compliance
              </h3>
              <p className="text-slate-400 mb-6">
                We help banks meet requirements across multiple regulatory frameworks:
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {['FFIEC', 'GLBA', 'SOX', 'PCI DSS', 'GDPR', 'CCPA', 'BSA/AML', 'FDIC'].map((reg) => (
                  <div key={reg} className="flex items-center gap-2 text-sm text-slate-300">
                    <Shield className="w-4 h-4 text-accent-400" />
                    {reg}
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="w-full" variant="glow">
                  Schedule Compliance Review
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}

